import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as statsApi from '../../api/statsApi';

// Initial state
const initialState = {
  overall: {
    totalRatings: 0,
    averageRating: 0,
    distribution: {
      1: { count: 0, percentage: 0 },
      2: { count: 0, percentage: 0 },
      3: { count: 0, percentage: 0 },
      4: { count: 0, percentage: 0 },
      5: { count: 0, percentage: 0 }
    }
  },
  categories: [],
  recentReviews: [],
  popularTags: [],
  loading: {
    overall: false,
    categories: false,
    recentReviews: false,
    popularTags: false
  },
  error: {
    overall: null,
    categories: null,
    recentReviews: null,
    popularTags: null
  },
  lastUpdated: null
};

// Async thunks
export const fetchOverallStats = createAsyncThunk(
  'stats/fetchOverallStats',
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await statsApi.getOverallStats(restaurantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCategoryStats = createAsyncThunk(
  'stats/fetchCategoryStats',
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await statsApi.getCategoryStats(restaurantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRecentReviews = createAsyncThunk(
  'stats/fetchRecentReviews',
  async ({limit = 5,restaurantId}, { rejectWithValue }) => {
    try {
      const response = await statsApi.getRecentReviews(limit, restaurantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPopularTags = createAsyncThunk(
  'stats/fetchPopularTags',
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await statsApi.getPopularTags(restaurantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// // Thunk to refresh all stats
// export const refreshAllStats = () => async (dispatch) => {
//   await Promise.all([
//     dispatch(fetchOverallStats()),
//     dispatch(fetchCategoryStats()),
//     dispatch(fetchRecentReviews()),
//     dispatch(fetchPopularTags())
//   ]);
// };

export const refreshAllStats = (restaurantId) => async (dispatch) => {
  await Promise.all([
    dispatch(fetchOverallStats(restaurantId)),
    dispatch(fetchCategoryStats(restaurantId)),
    dispatch(fetchRecentReviews({ restaurantId, limit: 5 })),
    dispatch(fetchPopularTags(restaurantId))
  ]);
};

// Slice
const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    clearStatsError: (state) => {
      state.error = {
        overall: null,
        categories: null,
        recentReviews: null,
        popularTags: null
      };
    },
    resetStats: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Overall stats
      .addCase(fetchOverallStats.pending, (state) => {
        state.loading.overall = true;
        state.error.overall = null;
      })
      .addCase(fetchOverallStats.fulfilled, (state, action) => {
        state.loading.overall = false;
        if (action.payload?.data) {
          state.overall = action.payload.data;
        }
        state.error.overall = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchOverallStats.rejected, (state, action) => {
        state.loading.overall = false;
        state.error.overall = action.payload || 'Failed to fetch overall stats';
      })
      
      // Category stats
      .addCase(fetchCategoryStats.pending, (state) => {
        state.loading.categories = true;
        state.error.categories = null;
      })
      .addCase(fetchCategoryStats.fulfilled, (state, action) => {
        state.loading.categories = false;
        state.categories = action.payload?.data || [];
        state.error.categories = null;
      })
      .addCase(fetchCategoryStats.rejected, (state, action) => {
        state.loading.categories = false;
        state.error.categories = action.payload || 'Failed to fetch category stats';
      })
      
      // Recent reviews
      .addCase(fetchRecentReviews.pending, (state) => {
        state.loading.recentReviews = true;
        state.error.recentReviews = null;
      })
      .addCase(fetchRecentReviews.fulfilled, (state, action) => {
        state.loading.recentReviews = false;
        state.recentReviews = action.payload?.data || [];
        state.error.recentReviews = null;
      })
      .addCase(fetchRecentReviews.rejected, (state, action) => {
        state.loading.recentReviews = false;
        state.error.recentReviews = action.payload || 'Failed to fetch recent reviews';
      })
      
      // Popular tags
      .addCase(fetchPopularTags.pending, (state) => {
        state.loading.popularTags = true;
        state.error.popularTags = null;
      })
      .addCase(fetchPopularTags.fulfilled, (state, action) => {
        state.loading.popularTags = false;
        state.popularTags = action.payload?.data || [];
        state.error.popularTags = null;
      })
      .addCase(fetchPopularTags.rejected, (state, action) => {
        state.loading.popularTags = false;
        state.error.popularTags = action.payload || 'Failed to fetch popular tags';
      });
  }
});

// Export actions
export const { clearStatsError, resetStats } = statsSlice.actions;

// Selectors
export const selectOverallStats = (state) => state.stats.overall;
export const selectCategoryStats = (state) => state.stats.categories;
export const selectRecentReviews = (state) => state.stats.recentReviews;
export const selectPopularTags = (state) => state.stats.popularTags;
export const selectStatsLoading = (state) => state.stats.loading;
export const selectStatsError = (state) => state.stats.error;
export const selectStatsLastUpdated = (state) => state.stats.lastUpdated;

// Export reducer
export default statsSlice.reducer;